import React from "react";
import { gql, useQuery } from "@apollo/client";
import type {
  BoundLogo,
  DocumentElement as DocumentElementType,
} from "@eaco/forms-core";

import { cn } from "../lib/utils";
import { useFormFillContext } from "../DocumentView/FormFillContext";
import { useMockBindings } from "../DocumentView/MockBindingsContext";

interface Props {
  element: DocumentElementType;
}

/**
 * Renders the form-owner's logo. Authenticated/internal fills query
 * `formDocument.ownerLogo` by formOid; anonymous public fills query
 * `publicForm.ownerLogo` by publicSlug. The server resolves logo preference:
 *   1. PDF logo (`logo_att_id`) from settings/business.
 *   2. Square avatar logo, when the PDF slot is empty.
 *   3. Null — we render the form owner's name as text instead.
 *
 * Accessible to anyone who can view the form (tenant gate is the form
 * owning client). The querying side stays light so cross-rendering
 * paths — Design canvas, Preview, server-rendered output — share the
 * same data path.
 */
const OWNER_LOGO_QUERY = gql`
  query formOwnerLogo($id: ID!) {
    formDocument(id: $id) {
      id
      ownerName
      ownerLogo {
        id
        name
        url
      }
    }
  }
`;

const PUBLIC_OWNER_LOGO_QUERY = gql`
  query publicFormOwnerLogo($publicSlug: String!) {
    publicForm(publicSlug: $publicSlug) {
      publicSlug
      ownerName
      ownerLogo {
        id
        name
        url
      }
    }
  }
`;

const SIZE_TO_HEIGHT_PX: Record<string, number> = {
  s: 32,
  m: 56,
  l: 96,
};

const extractStringValue = (raw: unknown, fallback: string): string => {
  if (typeof raw === "string" && raw !== "") return raw;
  if (Array.isArray(raw)) {
    const first = raw[0];
    if (
      first &&
      typeof first === "object" &&
      typeof (first as { value?: unknown }).value === "string"
    ) {
      return (first as { value: string }).value;
    }
  }
  return fallback;
};

const Logo: React.FC<Props> = ({ element }) => {
  const data = element.data as {
    size?: unknown;
    align?: unknown;
    boundLogo?: BoundLogo;
  };
  const sizeKey = extractStringValue(data?.size, "m");
  const height = SIZE_TO_HEIGHT_PX[sizeKey] ?? SIZE_TO_HEIGHT_PX.m;
  const align = extractStringValue(data?.align, "start");

  // Entity-report renders bake `data.boundLogo` server-side (there is no
  // formOid to query by); report design canvases provide a mock. Either
  // short-circuits the form-owner queries below.
  const mock = useMockBindings();
  const bound: BoundLogo | undefined = data?.boundLogo ?? mock?.logo;

  const { formOid, publicSlug } = useFormFillContext();
  const { data: formQueryData } = useQuery(OWNER_LOGO_QUERY, {
    variables: { id: formOid ?? "" },
    skip: !!bound || !formOid,
    fetchPolicy: "cache-first",
  });
  const { data: publicQueryData } = useQuery(PUBLIC_OWNER_LOGO_QUERY, {
    variables: { publicSlug: publicSlug ?? "" },
    skip: !!bound || !!formOid || !publicSlug,
    fetchPolicy: "cache-first",
  });

  const owner = formOid
    ? formQueryData?.formDocument
    : publicQueryData?.publicForm;

  const url: string | null = bound ? bound.url : owner?.ownerLogo?.url ?? null;
  const ownerName: string =
    (bound ? bound.ownerName : owner?.ownerName) || "Organisation";

  const justify =
    align === "center"
      ? "justify-center"
      : align === "end"
      ? "justify-end"
      : "justify-start";

  return (
    <div className={cn("flex w-full items-center", justify)}>
      {url ? (
        <img
          src={url}
          alt={`${ownerName} logo`}
          style={{ height, width: "auto" }}
          className="object-contain"
        />
      ) : (
        <span
          style={{ fontSize: Math.max(14, Math.round(height / 2.2)) }}
          className="font-semibold text-foreground leading-tight"
        >
          {ownerName}
        </span>
      )}
    </div>
  );
};

export default Logo;
